import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { getBorderColor } from 'src/lib/utils/cartUIHelpers'
import { BannerConfigType, BannerItemConfigType } from 'src/lib/types/customizeUI'

type BannerPosition = 'HEADER' | 'FOOTER'

interface CartBannerProps {
  bannerPosition?: BannerPosition
  cartBannerUIConfigData: BannerConfigType
  cartBottomBannerUIConfigData: BannerConfigType
}

export const CartBanner: React.FC<CartBannerProps> = ({
  bannerPosition = 'HEADER',
  cartBannerUIConfigData,
  cartBottomBannerUIConfigData
}) => {
  const config = bannerPosition === 'HEADER' ? cartBannerUIConfigData : cartBottomBannerUIConfigData

  const banners: BannerItemConfigType[] = config?.banners ?? []
  const isMultiple = !!(config?.multipleBannersEnabled && banners.length > 1)

  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    setActiveIndex(0)
  }, [banners.length, config?.animationStyle, isMultiple])

  const animationDelay = (config?.animationDelay ?? 1) * 1000

  useEffect(() => {
    if (!isMultiple) return
    const id = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % banners.length)
    }, animationDelay)
    return () => clearInterval(id)
  }, [isMultiple, banners.length, animationDelay])

  const getContainerStyle = useCallback(
    (bgColor: string | undefined, textColor: string | undefined) => {
      const borderColor = getBorderColor(bgColor?.substring(0, 7) ?? '#000000')
      if (bannerPosition === 'HEADER') {
        return {
          backgroundColor: bgColor,
          color: textColor,
          boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.2)',
          borderBottom: `1px solid ${borderColor}`
        }
      }
      return {
        backgroundColor: bgColor,
        color: textColor,
        boxShadow: '0px -1px 5px 0px rgba(0, 0, 0, 0.2)'
      }
    },
    [bannerPosition]
  )

  const containerClassName = useMemo(
    () =>
      bannerPosition === 'HEADER'
        ? 'flex min-h-[26px] w-full items-center justify-center px-3 py-1'
        : 'flex min-h-5 w-full items-center justify-center px-3',
    [bannerPosition]
  )

  const textClassName = useMemo(
    () => (bannerPosition === 'HEADER' ? 'text-center text-xs font-semibold' : 'text-center text-xs font-bold'),
    [bannerPosition]
  )

  if (!config || !config.enabled) return null

  const renderContent = () => {
    if (isMultiple) {
      const active = banners[activeIndex]

      if (config.animationStyle === 'horizontal_marquee') {
        return (
          <div className={containerClassName} style={getContainerStyle(active?.color, active?.textColor)}>
            <div className='overflow-hidden w-full flex justify-center'>
              <AnimatePresence mode='popLayout' initial={false}>
                <motion.div
                  key={activeIndex}
                  className={textClassName}
                  initial={{ x: '100%', opacity: 0, filter: 'blur(8px)' }}
                  animate={{ x: 0, opacity: 1, filter: 'blur(0px)' }}
                  exit={{ x: '-100%', opacity: 0, filter: 'blur(8px)' }}
                  transition={{ type: 'spring', duration: 0.6, bounce: 0.1 }}
                  dangerouslySetInnerHTML={{ __html: active?.text ?? '' }}
                />
              </AnimatePresence>
            </div>
          </div>
        )
      }

      // vertical_ticker (default)
      return (
        <div className={containerClassName} style={getContainerStyle(active?.color, active?.textColor)}>
          <div className='overflow-hidden w-full flex justify-center'>
            <AnimatePresence mode='popLayout' initial={false}>
              <motion.div
                key={activeIndex}
                className={textClassName}
                initial={{ y: '-100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '100%', opacity: 0 }}
                transition={{ type: 'spring', duration: 0.5, bounce: 0.1 }}
                dangerouslySetInnerHTML={{ __html: active?.text ?? '' }}
              />
            </AnimatePresence>
          </div>
        </div>
      )
    }

    return (
      <div className={containerClassName} style={getContainerStyle(config?.color, config?.textColor)}>
        <span className={textClassName} dangerouslySetInnerHTML={{ __html: config?.text }} />
      </div>
    )
  }

  return <div className='sticky top-0 z-20 overflow-hidden'>{renderContent()}</div>
}
